import { globalpermissions } from '../../../Services/Permission.js';

export const MODULE_ORDER = [
  'navigation',
  'pages',
  'organisation',
  'management',
  'campaign',
  'leads',
  'proposals',
  'supplier',
  'operations',
  'dashboard',
  'reporting',
  'templates',
  'ai',
  'onboarding',
  'settings',
  'other',
];

export const MODULE_LABELS = {
  navigation: 'Navigation & Menu',
  pages: 'Page Access',
  organisation: 'Organisation & Accounts',
  management: 'Management',
  campaign: 'Campaigns',
  leads: 'Leads & Forms',
  proposals: 'Proposals',
  supplier: 'Supplier & Booking',
  operations: 'Operations & Audit',
  dashboard: 'Dashboards',
  reporting: 'Reporting & Analytics',
  templates: 'Templates & Flow Builder',
  ai: 'AI & Tools',
  onboarding: 'Client Onboarding',
  settings: 'Settings & Profile',
  other: 'Other Permissions',
};

const OBJECT_ACTIONS = [
  { key: 'view', label: 'View' },
  { key: 'update', label: 'Update' },
  { key: 'create', label: 'Create' },
  { key: 'delete', label: 'Delete' },
];

let codenameModuleLookup;

function buildCodenameModuleLookup() {
  if (codenameModuleLookup) return codenameModuleLookup;

  const map = new Map();

  const register = (codename, moduleKey) => {
    if (typeof codename === 'string' && codename) {
      map.set(codename, moduleKey);
    }
  };

  const registerObject = (obj, moduleKey) => {
    Object.values(obj || {}).forEach((codename) => register(codename, moduleKey));
  };

  Object.entries(globalpermissions).forEach(([sectionKey, sectionValue]) => {
    if (!sectionValue || typeof sectionValue !== 'object') return;

    if (sectionKey === 'pagePermissions') {
      registerObject(sectionValue, 'pages');
      return;
    }
    if (sectionKey === 'navBar') {
      registerObject(sectionValue, 'navigation');
      return;
    }

    let moduleKey = 'other';
    if (sectionKey === 'homePage' || sectionKey === 'dashboard') moduleKey = 'dashboard';
    else if (sectionKey.includes('lead') || sectionKey === 'leadFormPage') moduleKey = 'leads';
    else if (sectionKey.includes('proposal') || sectionKey.includes('Proposal')) moduleKey = 'proposals';
    else if (sectionKey.includes('supplier') || sectionKey === 'supplierBookingPage') moduleKey = 'supplier';
    else if (sectionKey.includes('ops') || sectionKey.includes('audit')) moduleKey = 'operations';
    else if (sectionKey.includes('mapview') || sectionKey.includes('mapView')) moduleKey = 'dashboard';

    registerObject(sectionValue, moduleKey);
  });

  codenameModuleLookup = map;
  return map;
}

export function formatPermissionLabel(value = '') {
  return String(value)
    .split('_')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

function formatModuleLabel(moduleKey) {
  return MODULE_LABELS[moduleKey] || formatPermissionLabel(moduleKey);
}

export function inferModuleFromPermissionName(name = '') {
  const normalized = name.toLowerCase();

  if (normalized.includes('_on_menu') || normalized.includes('navbar') || normalized.includes('sidebar')) {
    return 'navigation';
  }
  if (normalized.includes('_page_access') || normalized.endsWith('_access')) return 'pages';
  if (normalized.includes('organisation') || normalized.includes('organization') || normalized.includes('account')) {
    return 'organisation';
  }
  if (normalized.includes('management')) return 'management';
  if (normalized.includes('campaign')) return 'campaign';
  if (normalized.includes('lead') || normalized.includes('web_form') || normalized.includes('b2b_form')) {
    return 'leads';
  }
  if (normalized.includes('proposal')) return 'proposals';
  if (normalized.includes('supplier') || normalized.includes('booking')) return 'supplier';
  if (normalized.includes('audit') || normalized.includes('ops_') || normalized.includes('opsexecution')) {
    return 'operations';
  }
  if (normalized.includes('dashboard') || normalized.includes('homepage') || normalized.includes('benchmarking')) {
    return 'dashboard';
  }
  if (normalized.includes('report')) return 'reporting';
  if (normalized.includes('template') || normalized.includes('flow_builder') || normalized.includes('intervene')) {
    return 'templates';
  }
  if (normalized.includes('ai_') || normalized.includes('vector')) return 'ai';
  if (normalized.includes('onboarding')) return 'onboarding';
  if (normalized.includes('setting') || normalized.includes('password') || normalized.includes('profile_account')) {
    return 'settings';
  }

  return 'other';
}

export function resolvePermissionModule(permission) {
  const lookup = buildCodenameModuleLookup();
  const codename = permission.codename || permission.name;
  const inferred = inferModuleFromPermissionName(permission.name);

  if (inferred !== 'other' && inferred !== 'pages') {
    return inferred;
  }

  return lookup.get(codename) || inferred;
}

export function groupGeneralPermissions(permissions = []) {
  const groups = new Map();

  permissions.forEach((permission) => {
    const moduleKey = resolvePermissionModule(permission);
    if (!groups.has(moduleKey)) groups.set(moduleKey, []);
    groups.get(moduleKey).push(permission);
  });

  const orderedKeys = [
    ...MODULE_ORDER.filter((key) => groups.has(key)),
    ...[...groups.keys()].filter((key) => !MODULE_ORDER.includes(key)),
  ];

  return orderedKeys.map((moduleKey) => {
    const modulePermissions = groups.get(moduleKey) || [];
    return {
      moduleKey,
      moduleLabel: formatModuleLabel(moduleKey),
      permissions: modulePermissions,
      enabledCount: modulePermissions.filter((item) => item.isAllowed).length,
      totalCount: modulePermissions.length,
    };
  });
}

export function groupObjectPermissions(permissions = []) {
  return permissions.map((permission) => ({
    ...permission,
    moduleLabel: formatPermissionLabel(permission.name),
    enabledCount: OBJECT_ACTIONS.filter((action) => permission[action.key]).length,
    totalCount: OBJECT_ACTIONS.length,
  }));
}

export { OBJECT_ACTIONS };
